import { useState, useMemo, type FormEvent } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@pantry/ui';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { formatDate, parseLocalDate } from '@pantry/shared';
import { normalizeBarcode } from '@pantry/shared/utils';
import type { SelectedProduct, ItemWithRelations } from '@pantry/shared';
import type { ItemUnit, Product } from '@pantry/api-client';

import type { UnifiedItemFormProps } from './types';
import { ProductSearchSection } from './ProductSearchSection';
import { ItemFieldsSection } from './ItemFieldsSection';
import { InstanceFieldsSection } from './InstanceFieldsSection';
import { TranslationIndicator } from '../../products/TranslationIndicator';
import { ProductInternationalNames } from '../../products/ProductInternationalNames';
import { useProductName, useProductTranslationStatus } from '../../../hooks/useProductLanguage';

/**
 * Unified item form component that renders sections based on mode.
 * Does NOT include Dialog/Sheet wrapper -- consumers provide that.
 */
export function UnifiedItemForm(props: UnifiedItemFormProps) {
  const { householdId, isPending, error, onCancel, mode } = props;
  const { t } = useLingui();

  // Derive initial values based on mode
  const item = mode === 'edit-item' ? props.item : undefined;
  const instance = mode === 'edit-instance' ? props.instance : undefined;
  const defaultProductSearch = mode === 'create-item' ? props.defaultProductSearch : undefined;
  const initialValues = mode === 'create-item' ? props.initialValues : undefined;

  // Build initial selected product from item when editing
  const initialSelectedProduct: SelectedProduct = useMemo(() => {
    if (initialValues?.selectedProduct) return initialValues.selectedProduct;
    if (!item?.product) return null;
    const product = item.product;
    return {
      type: 'local' as const,
      product: {
        id: product.id,
        name: product.name,
        brand: product.brand || null,
        barcode: product.barcode || null,
        description: product.description || null,
        imageUrl: product.imageUrl || null,
        category: product.category || null,
        source: product.source || 'user',
        defaultQuantity: product.defaultQuantity || null,
        defaultUnit: product.defaultUnit || null,
        names: product.names ?? null,
      },
    };
  }, [item, initialValues]);

  // -- Form state --
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct>(initialSelectedProduct);
  const [customName, setCustomName] = useState(initialValues?.customName ?? item?.customName ?? '');
  const [unit, setUnit] = useState<ItemUnit>(initialValues?.unit ?? item?.unit ?? 'item');
  const [storageLocationId, setStorageLocationId] = useState(
    initialValues?.storageLocationId ?? item?.storageLocationId ?? ''
  );
  const [barcode, setBarcode] = useState(initialValues?.barcode ?? '');
  const [itemNotes, setItemNotes] = useState(initialValues?.notes ?? item?.notes ?? '');

  // Quantity is a string in ItemFieldsSection but a number in InstanceFieldsSection
  const [itemQuantity, setItemQuantity] = useState(
    String(initialValues?.quantity ?? item?.quantity ?? 1)
  );

  // Instance-level state
  const [instanceQuantity, setInstanceQuantity] = useState(instance?.quantity ?? 1);
  const [purchaseDate, setPurchaseDate] = useState<Date | undefined>(
    instance?.purchaseDate
      ? parseLocalDate(instance.purchaseDate)
      : initialValues?.purchaseDate
        ? parseLocalDate(initialValues.purchaseDate)
        : mode === 'create-item' || mode === 'create-instance'
          ? new Date()
          : undefined
  );
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(
    instance?.expiryDate
      ? parseLocalDate(instance.expiryDate)
      : initialValues?.expiryDate
        ? parseLocalDate(initialValues.expiryDate)
        : undefined
  );
  const [instanceNotes, setInstanceNotes] = useState(instance?.notes || '');

  // Local validation error state for create-item mode
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (mode === 'create-item') {
      setLocalError(null);

      if (!selectedProduct && !customName.trim()) {
        setLocalError(t`Please select a product or enter a custom name`);
        return;
      }
      if (!storageLocationId) {
        setLocalError(t`Please select a storage location`);
        return;
      }

      await props.onSubmit({
        selectedProduct,
        customName: customName.trim(),
        barcode: barcode.trim() ? normalizeBarcode(barcode.trim()) : undefined,
        unit,
        storageLocationId: storageLocationId || null,
        quantity: parseInt(itemQuantity, 10) || 1,
        purchaseDate: purchaseDate ? formatDate(purchaseDate) : undefined,
        expiryDate: expiryDate ? formatDate(expiryDate) : undefined,
        notes: itemNotes.trim() || null,
      });
    } else if (mode === 'edit-item') {
      setLocalError(null);
      if (!selectedProduct && !customName.trim()) {
        setLocalError(t`Please select a product or enter a custom name`);
        return;
      }
      await props.onSubmit({
        selectedProduct,
        customName: customName.trim(),
        unit,
        storageLocationId: storageLocationId || null,
        quantity: parseInt(itemQuantity, 10) || 1,
        notes: itemNotes.trim() || null,
      });
    } else if (mode === 'create-instance' || mode === 'edit-instance') {
      await props.onSubmit({
        quantity: instanceQuantity,
        purchaseDate: purchaseDate ? formatDate(purchaseDate) : undefined,
        expiryDate: expiryDate ? formatDate(expiryDate) : undefined,
        notes: instanceNotes.trim() || null,
      });
    }
  };

  // "Change Product" toggle for edit-item mode
  const [showChangeProduct, setShowChangeProduct] = useState(false);

  const showProductSearch =
    mode === 'create-item' || showChangeProduct || (mode === 'edit-item' && !item?.product);
  const showItemFields = mode === 'create-item' || mode === 'edit-item';
  const showInstanceFields =
    mode === 'create-item' || mode === 'create-instance' || mode === 'edit-instance';
  const showBarcode = mode === 'create-item';
  const showQuantityPresets = mode === 'create-instance' || mode === 'edit-instance';

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-tour="inventory-item-form">
      {/* Error banner */}
      {(error || localError) && (
        <div className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2 duration-200">
          {error || localError}
        </div>
      )}

      {/* Product search (create-item or change-product in edit) */}
      {showProductSearch && (
        <ProductSearchSection
          householdId={householdId}
          selectedProduct={selectedProduct}
          onProductSelect={setSelectedProduct}
          customName={customName}
          onCustomNameChange={setCustomName}
          defaultProductSearch={defaultProductSearch}
          allowExternalProducts={mode !== 'edit-item'}
          onQuantityChange={(q) => setItemQuantity(String(q))}
          onUnitChange={setUnit}
        />
      )}

      {/* Read-only product card (edit-item with product) */}
      {mode === 'edit-item' && item?.product && !showChangeProduct && (
        <ReadOnlyProductCard
          product={item.product}
          onChangeProduct={() => setShowChangeProduct(true)}
        />
      )}

      {/* Item fields (create-item, edit-item) */}
      {showItemFields && (
        <ItemFieldsSection
          householdId={householdId}
          customName={customName}
          onCustomNameChange={setCustomName}
          unit={unit}
          onUnitChange={setUnit}
          storageLocationId={storageLocationId}
          onStorageLocationIdChange={setStorageLocationId}
          barcode={barcode}
          onBarcodeChange={setBarcode}
          showBarcode={showBarcode}
          notes={itemNotes}
          onNotesChange={setItemNotes}
          quantity={itemQuantity}
          onQuantityChange={setItemQuantity}
          hasProduct={!!selectedProduct}
        />
      )}

      {/* Instance fields (create-item, create-instance, edit-instance) */}
      {showInstanceFields && (
        <InstanceFieldsSection
          quantity={mode === 'create-item' ? parseInt(itemQuantity, 10) || 1 : instanceQuantity}
          onQuantityChange={
            mode === 'create-item' ? (q) => setItemQuantity(String(q)) : setInstanceQuantity
          }
          purchaseDate={purchaseDate}
          onPurchaseDateChange={setPurchaseDate}
          expiryDate={expiryDate}
          onExpiryDateChange={setExpiryDate}
          notes={mode === 'create-instance' || mode === 'edit-instance' ? instanceNotes : undefined}
          onNotesChange={
            mode === 'create-instance' || mode === 'edit-instance' ? setInstanceNotes : undefined
          }
          showQuantityPresets={showQuantityPresets}
          showQuantity={mode !== 'create-item'}
        />
      )}

      {/* Action buttons */}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          <Trans>Cancel</Trans>
        </Button>
        <Button type="submit" disabled={isPending} data-tour="inventory-form-submit">
          {isPending ? (
            <>
              <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
              <Trans>Saving...</Trans>
            </>
          ) : mode === 'create-item' || mode === 'create-instance' ? (
            <Trans context="action_button">Add</Trans>
          ) : (
            <Trans context="action_button">Save</Trans>
          )}
        </Button>
      </div>
    </form>
  );
}

/**
 * Read-only product display card shown in edit-item mode
 * when the item has an associated product.
 */
function ReadOnlyProductCard({
  product,
  onChangeProduct,
}: {
  product: NonNullable<ItemWithRelations['product']>;
  onChangeProduct?: () => void;
}) {
  const translationProduct = product as unknown as Product;
  const resolvedName = useProductName(translationProduct);
  const translationStatus = useProductTranslationStatus(translationProduct);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 rounded-xl border border-muted bg-muted/30 p-3">
        {product.imageUrl && (
          <img
            src={product.imageUrl}
            alt={resolvedName || product.name}
            className="h-10 w-10 rounded-lg object-cover"
          />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium">{resolvedName || product.name}</p>
            <span className="inline-flex items-center gap-1 rounded-full bg-fresh/10 px-2 py-0.5 text-xs text-fresh dark:bg-fresh/20">
              <CheckCircleIcon className="h-3 w-3" />
              <Trans>Linked Product</Trans>
            </span>
          </div>
          {product.brand && <p className="text-sm text-muted-foreground mt-0.5">{product.brand}</p>}
        </div>
        {onChangeProduct && (
          <button
            type="button"
            onClick={onChangeProduct}
            className="text-sm text-primary hover:text-primary/80 font-medium shrink-0"
          >
            <Trans context="action_button">Change</Trans>
          </button>
        )}
      </div>
      {translationStatus.canContribute && resolvedName && (
        <TranslationIndicator
          product={translationProduct}
          currentName={resolvedName}
          variant="inline"
        />
      )}
      <ProductInternationalNames product={translationProduct} variant="inline" />
    </div>
  );
}
